"use client";

import React, { useCallback, ChangeEvent, useEffect } from "react";

import { useMediaQuery } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import clsx from "clsx";

import { BffComponentType } from "~/bff/ComponentsConfig";
import { SearchPanel } from "~/bff/types/SearchPanel";
import { SearchField } from "~/components/search-field/component";
import { useSearchPopover } from "~/components/search-panel/hooks/use-search-popover/hook";
import { UniversalComponent } from "~/components/universal-component/component";
import { BffComponentExtended } from "~/components/universal-component/types";
import { searchPanelConfig } from "~/constants/components-configs/search-panel-config";
import {
  SearchDataContext,
  SearchVisibilityContext,
} from "~/context/search/context";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Close24 } from "~/theme/icons/components/close";
import { Search24 } from "~/theme/icons/components/search";
import { getFormatMedia } from "~/theme/utils";
import { Nullable } from "~/types/general.types";

import { MOBILE_SEARCH_POPOVER_ID, SEARCH_PANEL_MOBILE_CLASSES } from "./constants";
import { SearchPanelMobileContainer, SearchPanelMobilePopover } from "./styled";

export interface SearchPanelMobileProps {
  searchPanel?: Nullable<SearchPanel>;
  className?: string;
}

const SEARCH_BUTTON_ID = "search-button";

const SearchPanelMobileComponent: React.FC<SearchPanelMobileProps> = ({
  searchPanel,
  className,
}) => {
  const theme = useTheme();
  const { BREAKPOINT_DESKTOP } = getFormatMedia(theme);
  const isDesktopView = useMediaQuery(BREAKPOINT_DESKTOP);
  const {
    query,
    queryRevision,
    suggestions,
    handleQueryChange,
    handleQueryClear,
    handleSearchClick,
  } = React.useContext(SearchDataContext) ?? {};
  const { isSearchOpen, handleSearchClose, handleSearchOpen } =
    React.useContext(SearchVisibilityContext) ?? {};
  const { handleToggle, popoverRef, supportsPopover } = useSearchPopover({
    breakpoint: BREAKPOINT_DESKTOP,
    handleSearchClose,
    handleSearchOpen,
    isDesktopView,
    isSearchOpen,
    viewport: "mobile",
  });
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (handleQueryChange) {
        handleQueryChange(event.target.value);
      }
    },
    [handleQueryChange],
  );

  const handleSearch = useCallback(() => {
    if (handleSearchClick) {
      handleSearchClick();
    }
    if (handleSearchClose) {
      handleSearchClose();
    }
  }, [handleSearchClick, handleSearchClose]);

  const onCloseButtonClick = useCallback(() => {
    handleSearchClose?.();
  }, [handleSearchClose]);

  useEffect(() => {
    if (isSearchOpen && !isDesktopView && supportsPopover !== undefined) {
      const frameId = requestAnimationFrame(() => {
        const panel = supportsPopover
          ? popoverRef.current
          : document.querySelector<HTMLElement>(
              '[data-testautomation-id="search-panel"]',
            );
        const input = panel?.querySelector<HTMLInputElement>(
          '[data-testautomation-id="search-field-input"]',
        );
        input?.focus();
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [isDesktopView, isSearchOpen, popoverRef, supportsPopover]);

  useEffect(() => {
    if (!isSearchOpen || isDesktopView || supportsPopover !== true) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDesktopView, isSearchOpen, supportsPopover]);

  if (isDesktopView) {
    return;
  }

  const panelContent = (
    <>
      <Toolbar className={SEARCH_PANEL_MOBILE_CLASSES.toolbar} role="search">
        <IconButton
          color="inherit"
          aria-label="close search panel"
          onClick={onCloseButtonClick}
          popoverTarget={
            supportsPopover === false ? undefined : MOBILE_SEARCH_POPOVER_ID
          }
          popoverTargetAction={supportsPopover === false ? undefined : "hide"}
          {...getTestAutomationProps("close-button")}
          size="large"
        >
          <Close24 />
        </IconButton>
        <SearchField
          withIcon={false}
          value={query}
          valueRevision={queryRevision}
          isSuggestionsOpen={Boolean(suggestions?.length)}
          onChange={handleChange}
          onHydratedValue={handleQueryChange}
          onClear={handleQueryClear}
          onSearch={handleSearch}
          onEscape={handleSearchClose}
          translations={searchPanel?.props?.translations}
        />
        <IconButton
          color="inherit"
          aria-label={
            searchPanel?.props?.translations?.searchText || SEARCH_BUTTON_ID
          }
          onClick={handleSearch}
          {...getTestAutomationProps(SEARCH_BUTTON_ID)}
          size="large"
        >
          <Search24 />
        </IconButton>
      </Toolbar>
      {isSearchOpen && searchPanel && (
        <UniversalComponent
          meta={searchPanel._meta}
          props={searchPanel.props}
          component={searchPanel.component ? BffComponentType.SEARCH_PANEL : null}
          elements={searchPanel.children as BffComponentExtended[]}
          componentsConfigs={[searchPanelConfig]}
        />
      )}
    </>
  );

  if (supportsPopover === false) {
    return (
      <SearchPanelMobileContainer
        keepMounted
        slotProps={{
          paper: {
            className: clsx(SEARCH_PANEL_MOBILE_CLASSES.drawer, className),
            ...getTestAutomationProps("search-panel"),
            inert: !isSearchOpen || undefined,
            "aria-hidden": !isSearchOpen,
          },
        }}
        anchor="right"
        open={isSearchOpen}
        onClose={handleSearchClose}
      >
        {panelContent}
      </SearchPanelMobileContainer>
    );
  }

  return (
    <SearchPanelMobilePopover
      ref={popoverRef}
      id={MOBILE_SEARCH_POPOVER_ID}
      popover="auto"
      role="dialog"
      aria-label="Search panel"
      className={className}
      onToggle={handleToggle}
      {...getTestAutomationProps("search-panel")}
    >
      {panelContent}
    </SearchPanelMobilePopover>
  );
};

export const SearchPanelMobile = React.memo(SearchPanelMobileComponent);
